// @erp/module-cms/fe · queryOptions accessor-factories (DEV-GUIDE-002 §3.2).
// Mirrors modules/lib/src/fe/queries.ts.
//
// THE PATTERN these prove: each factory takes PLAIN values (never an accessor)
// and is CALLED inside the options function at the screen —
//   const search = Route.useSearch();                             // accessor
//   const q = useQuery(() => cmsQueries.announcementsList(search()));  // called here
// Because the whole options function re-runs when search() changes, reactivity
// lives at the call site and the factory stays a pure value→queryOptions map.
// (Passing `cmsQueries.announcementsList(search())` straight into useQuery — no
// arrow — snapshots the filters once and the query never re-keys. §3.2's sin.)
//
// Every queryFn is `() => unwrap<DTO>(cmsApi.<path>.$get(...))`: the typed hc
// client builds the request, unwrap resolves ok→JSON or throws the wire's
// ApiError (@erp/api-client). DTOs are the schema-inferred wire types — one
// contract for validator, service return, and this cache.
//
// staleTime tiers (ADR-001 §10.2 — factories override the portal's 30s default):
//   list      30s  — the listings default; a page is fine slightly stale
//   detail    60s  — a single row churns less than a filtered list
//   reference  5m  — registries/config (categories, settings — rarely edited)
// keepPreviousData on the keyset list holds the current page visible while the
// next cursor loads (no flash to empty on paginate/filter).
import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/solid-query";
import { ApiError, unwrap } from "@erp/api-client";
import type {
  CmsAnnouncement,
  CmsAnnouncementListQuery,
  CmsCategory,
  CmsCategoryListQuery,
  CmsCategoryType,
  CmsConsoleStats,
  CmsContentStats,
  CmsCookie,
  CmsCookieListQuery,
  CmsEvent,
  CmsEventListQuery,
  CmsGalleryItem,
  CmsGalleryItemListQuery,
  CmsPublication,
  CmsPublicationListQuery,
  CmsPublicationTitle,
  CmsPublicationTitleListQuery,
  CmsPublicPublication,
  CmsPublicSettings,
  CmsRevision,
  CmsRevisionDetail,
  CmsSettingKey,
  CmsSubscriber,
  CmsSubscriberListQuery,
  CmsSubscriberStats,
  CmsTender,
  CmsTenderListQuery,
  Media,
  MediaListQuery,
  MediaServe,
  ReadingFile,
  RevisionEntityType,
} from "../schemas/index.js";
import { cmsApi } from "./client.js";
import { cmsKeys } from "./keys.js";

/** Keyset page envelope (@erp/platform/db keysetPage) — every cursor-paged list returns this. */
export type KeysetPage<T> = { data: T[]; nextCursor: string | null };

/**
 * The settings wire shape — a flat Record of TEXT scalars (the store speaks
 * strings end to end; parse at the edge that needs a number). The admin read
 * (readSettings) returns EVERY known key merged over DEFAULT_SETTINGS; the public
 * read is an allowlisted subset, so it is a Partial of the same map.
 */
export type CmsSettings = Record<CmsSettingKey, string>;

/**
 * GET /reading/:slug's 200 body (the reading gate, ADR-010 §2.12): the ORDINARY public
 * publication DTO (`readAccess`, sanitised body, media URLs — one shape wherever a
 * publication appears), the presigned `file` (null when the row has no pdf; a 15-minute
 * inline GET for a PDF, `attachment` for any other type) and whether a Download button is
 * allowed. Mirrors the service's ReadingResponse WITHOUT importing worker code (the
 * client.ts import-type law): both ends are built from the same schemas.
 */
export interface CmsReadingResponse {
  publication: CmsPublicPublication;
  file: ReadingFile | null;
  download: boolean;
}

/**
 * How long before a presigned reader URL dies the reader asks for a fresh one — 60 s.
 * The refetch is what keeps a long read alive: a PDF viewer fetches pages lazily (range
 * requests), so an expired URL breaks paging in the middle of a document, not just the
 * first load. Refetching a minute early leaves a dead URL nowhere in the window.
 */
export const READING_REFRESH_MARGIN_MS = 60_000;

/** Never refetch faster than this, whatever the wire says about expiry — a clock skewed by hours must not spin the reader. */
const READING_REFRESH_FLOOR_MS = 30_000;

/**
 * Ms until the reader's file should be re-minted — `expiresAt − margin − now`, floored:
 * the SAME arithmetic feeds staleTime (a remount or a focus after the window re-reads)
 * and refetchInterval (a tab left open re-reads on its own). No file ⇒ nothing expires.
 */
export const readingRefreshInMs = (data: CmsReadingResponse | undefined, now: number = Date.now()): number | false =>
  data?.file ? Math.max(READING_REFRESH_FLOOR_MS, data.file.expiresAt - READING_REFRESH_MARGIN_MS - now) : false;

const STALE = {
  list: 30_000,
  detail: 60_000,
  reference: 5 * 60_000,
} as const;

/**
 * A by-slug sibling lookup where "not found" is an ANSWER, not an error: the translate
 * affordance reads `null` as "no Hindi version yet — offer to create one", so the 404 the
 * wire uses for absence resolves to null here instead of putting the query into its error
 * state (and retrying it). Every other status still throws the wire's ApiError.
 */
const nullOn404 = async <T>(p: Promise<T>): Promise<T | null> => {
  try {
    return await p;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
};

/**
 * The revision routes hang off the FOUR rich content types (/{type}/:id/revisions[/:revNo]),
 * so the typed hc path differs per type even though every response shares the CmsRevision /
 * CmsRevisionDetail shape. These two builders switch the discriminator ONCE, so the
 * revisionsList/revision factories (and the useRestoreRevision hook, which reuses the same
 * discipline) stay a single generic (type, id[, revNo]) surface. Exhaustive over
 * RevisionEntityType — a new content type that gains a History log adds a case here.
 */
const revisionsListReq = (type: RevisionEntityType, id: string) => {
  const param = { id };
  switch (type) {
    case "announcement":
      return cmsApi.announcements[":id"].revisions.$get({ param });
    case "tender":
      return cmsApi.tenders[":id"].revisions.$get({ param });
    case "event":
      return cmsApi.events[":id"].revisions.$get({ param });
    case "publication":
      return cmsApi.publications[":id"].revisions.$get({ param });
    case "publication_title":
      return cmsApi["publication-titles"][":id"].revisions.$get({ param });
  }
};

const revisionReq = (type: RevisionEntityType, id: string, revNo: number) => {
  // :revNo rides the URL as a string; the route's revNoParam re-parses it to a positive int.
  const param = { id, revNo: String(revNo) };
  switch (type) {
    case "announcement":
      return cmsApi.announcements[":id"].revisions[":revNo"].$get({ param });
    case "tender":
      return cmsApi.tenders[":id"].revisions[":revNo"].$get({ param });
    case "event":
      return cmsApi.events[":id"].revisions[":revNo"].$get({ param });
    case "publication":
      return cmsApi.publications[":id"].revisions[":revNo"].$get({ param });
    case "publication_title":
      return cmsApi["publication-titles"][":id"].revisions[":revNo"].$get({ param });
  }
};

/** The categories read (hoisted so categoriesByType can alias it without a self-referencing initializer). */
const categoriesList = (filters: CmsCategoryListQuery) =>
  queryOptions({
    queryKey: cmsKeys.categories.list(filters.type, filters.locale),
    queryFn: () =>
      unwrap<CmsCategory[]>(
        cmsApi.categories.$get({
          query: { ...(filters.type ? { type: filters.type } : {}), ...(filters.locale ? { locale: filters.locale } : {}) },
        }),
      ),
    staleTime: STALE.reference,
  });

export const cmsQueries = {
  // ── Announcements ────────────────────────────────────────────────────────────
  /** Keyset list, filters (?type/?status/?categoryId + cursor) ⊂ the key ⇒ the URL drives the cache. keepPreviousData across pages. */
  announcementsList: (filters: CmsAnnouncementListQuery) =>
    queryOptions({
      queryKey: cmsKeys.announcements.list(filters),
      queryFn: () => unwrap<KeysetPage<CmsAnnouncement>>(cmsApi.announcements.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),


/**
 * THE INFINITE TWIN of a keyset list (2026-09-22, the owner: "now let's do it
 * with every table and card listing of every portal").
 *
 * Every `<x>Infinite` factory below reads the SAME endpoint, with the SAME
 * projection and the SAME filters, as the `<x>List` beside it — the only
 * difference is that its pages ACCUMULATE in one cache entry instead of
 * replacing each other. No API changed for any of this.
 *
 * · `initialPageParam: undefined` — the first page carries no cursor, exactly
 *   as the first `?cursor=`-less request always did.
 * · `getNextPageParam` reads the wire's own `nextCursor`; `null` (the last
 *   page) becomes `undefined`, which is how TanStack says "no more".
 * · The filters are the key MINUS the cursor — a cursor is a position inside
 *   the stream now, not part of its identity, so it has left the URL and the
 *   key alike. `"infinite"` on the end keeps the entry distinct from the paged
 *   `<x>List(filters)` one, which the forms' pickers still use.
 * · NO `keepPreviousData`: a filter change starts a NEW stream, and holding the
 *   old rows under the new controls would show a list that does not match them.
 *   The table paints its skeleton rows instead.
 */

  /** The notice feed as an INFINITE stream — the head-note above is the contract for every twin here. */
  announcementsListInfinite: (filters: Omit<CmsAnnouncementListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...cmsKeys.announcements.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<CmsAnnouncement>>(cmsApi.announcements.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<CmsAnnouncement>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /**
   * THE CONSOLE'S FIGURES — GET /stats, one row per content section the session may
   * read (2026-10-05, the owner: the CMS home "should have stats just like libraries
   * … available per sub module").
   *
   * One request for the whole hub. The home could have fired the six per-type
   * `*Stats` queries below and added them up, but that puts the arithmetic in the
   * browser and the totals a render behind each other; the server counts once.
   * A section this session cannot read is ABSENT from `sections`, never zero.
   */
  consoleStats: () =>
    queryOptions({
      queryKey: cmsKeys.consoleStats(),
      queryFn: () => unwrap<CmsConsoleStats>(cmsApi.stats.$get()),
      staleTime: STALE.list,
    }),

  /** The list's figures — GET /announcements/stats, institution-wide status counts (never a page). Under announcements.all(), so every write that sweeps the domain refreshes them. Reads at the list's staleness. */
  announcementsStats: () =>
    queryOptions({
      queryKey: cmsKeys.announcements.stats(),
      queryFn: () => unwrap<CmsContentStats>(cmsApi.announcements.stats.$get()),
      staleTime: STALE.list,
    }),

  /** A single announcement — the detail view + the edit form's defaultValues source (404s on a trashed row). */
  announcement: (id: string) =>
    queryOptions({
      queryKey: cmsKeys.announcements.detail(id),
      queryFn: () => unwrap<CmsAnnouncement>(cmsApi.announcements[":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  /**
   * The LIVE row at (slug, locale) in ANY status, or null when there is none — the
   * translation affordance's sibling lookup (GET /announcements/by-slug/:slug?locale=).
   * The route validates the body in-handler, so hc types the input as `{ param }` only;
   * binding the args to a const carries the typed `query` through (the mutations.ts
   * precedent) and hc appends it at runtime.
   */
  announcementBySlug: (slug: string, locale: string) =>
    queryOptions({
      queryKey: cmsKeys.announcements.bySlug(slug, locale),
      queryFn: () => {
        const args = { param: { slug }, query: { locale } };
        return nullOn404(unwrap<CmsAnnouncement>(cmsApi.announcements["by-slug"][":slug"].$get(args)));
      },
      staleTime: STALE.detail,
    }),

  // ── Tenders ──────────────────────────────────────────────────────────────────
  /** Keyset list, filters (?status/?categoryId/?isSimple/?parentId + cursor) ⊂ the key ⇒ the URL drives the cache. keepPreviousData across pages. */
  tendersList: (filters: CmsTenderListQuery) =>
    queryOptions({
      queryKey: cmsKeys.tenders.list(filters),
      queryFn: () => unwrap<KeysetPage<CmsTender>>(cmsApi.tenders.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),


  /** The tender register as an INFINITE stream — see `announcementsListInfinite`. */
  tendersListInfinite: (filters: Omit<CmsTenderListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...cmsKeys.tenders.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<CmsTender>>(cmsApi.tenders.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<CmsTender>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** The list's figures — GET /tenders/stats (the announcementsStats precedent). */
  tendersStats: () =>
    queryOptions({
      queryKey: cmsKeys.tenders.stats(),
      queryFn: () => unwrap<CmsContentStats>(cmsApi.tenders.stats.$get()),
      staleTime: STALE.list,
    }),

  /** A single tender — the detail view + the edit form's defaultValues source (404s on a trashed row). */
  tender: (id: string) =>
    queryOptions({
      queryKey: cmsKeys.tenders.detail(id),
      queryFn: () => unwrap<CmsTender>(cmsApi.tenders[":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  // ── Events ───────────────────────────────────────────────────────────────────
  /** Keyset list, filters (?status/?categoryId/?featured/?upcoming + cursor) ⊂ the key ⇒ the URL drives the cache. keepPreviousData across pages. */
  eventsList: (filters: CmsEventListQuery) =>
    queryOptions({
      queryKey: cmsKeys.events.list(filters),
      queryFn: () => unwrap<KeysetPage<CmsEvent>>(cmsApi.events.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),


  /** The events list as an INFINITE stream — see `announcementsListInfinite`. */
  eventsListInfinite: (filters: Omit<CmsEventListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...cmsKeys.events.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<CmsEvent>>(cmsApi.events.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<CmsEvent>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** The list's figures — GET /events/stats (the announcementsStats precedent). */
  eventsStats: () =>
    queryOptions({
      queryKey: cmsKeys.events.stats(),
      queryFn: () => unwrap<CmsContentStats>(cmsApi.events.stats.$get()),
      staleTime: STALE.list,
    }),

  /** A single event — the detail view + the edit form's defaultValues source (404s on a trashed row). */
  event: (id: string) =>
    queryOptions({
      queryKey: cmsKeys.events.detail(id),
      queryFn: () => unwrap<CmsEvent>(cmsApi.events[":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  /** The LIVE event at (slug, locale) in any status, or null — the sibling lookup (the announcementBySlug precedent). */
  eventBySlug: (slug: string, locale: string) =>
    queryOptions({
      queryKey: cmsKeys.events.bySlug(slug, locale),
      queryFn: () => {
        const args = { param: { slug }, query: { locale } };
        return nullOn404(unwrap<CmsEvent>(cmsApi.events["by-slug"][":slug"].$get(args)));
      },
      staleTime: STALE.detail,
    }),

  // ── Publications ─────────────────────────────────────────────────────────────
  /** Keyset list, filters (?docType/?status/?categoryId/?activeStatus + cursor) ⊂ the key ⇒ the URL drives the cache. keepPreviousData across pages. */
  publicationsList: (filters: CmsPublicationListQuery) =>
    queryOptions({
      queryKey: cmsKeys.publications.list(filters),
      queryFn: () => unwrap<KeysetPage<CmsPublication>>(cmsApi.publications.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),


  /** The publications list as an INFINITE stream — see `announcementsListInfinite`. */
  publicationsListInfinite: (filters: Omit<CmsPublicationListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...cmsKeys.publications.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<CmsPublication>>(cmsApi.publications.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<CmsPublication>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** The list's figures — GET /publications/stats (the announcementsStats precedent). */
  publicationsStats: () =>
    queryOptions({
      queryKey: cmsKeys.publications.stats(),
      queryFn: () => unwrap<CmsContentStats>(cmsApi.publications.stats.$get()),
      staleTime: STALE.list,
    }),

  /** A single publication — the detail view + the edit form's defaultValues source (404s on a trashed row). */
  publication: (id: string) =>
    queryOptions({
      queryKey: cmsKeys.publications.detail(id),
      queryFn: () => unwrap<CmsPublication>(cmsApi.publications[":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  /** The LIVE publication at (slug, locale) in any status, or null — the sibling lookup (the announcementBySlug precedent). */
  publicationBySlug: (slug: string, locale: string) =>
    queryOptions({
      queryKey: cmsKeys.publications.bySlug(slug, locale),
      queryFn: () => {
        const args = { param: { slug }, query: { locale } };
        return nullOn404(unwrap<CmsPublication>(cmsApi.publications["by-slug"][":slug"].$get(args)));
      },
      staleTime: STALE.detail,
    }),

  // ── Publication titles (0022) ────────────────────────────────────────────────
  /** Keyset list, filters (?kind/?status/?runStatus/?locale/?q + cursor) ⊂ the key ⇒ the URL drives the cache. keepPreviousData across pages. The issue form's picker reads it too (kind + locale + q). */
  publicationTitlesList: (filters: CmsPublicationTitleListQuery) =>
    queryOptions({
      queryKey: cmsKeys.publicationTitles.list(filters),
      queryFn: () => unwrap<KeysetPage<CmsPublicationTitle>>(cmsApi["publication-titles"].$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),


  /** The title registry as an INFINITE stream — the LIST screen's source. The issue form's picker keeps the paged `publicationTitlesList`: it asks for one capped page and throws the cursor away. */
  publicationTitlesListInfinite: (filters: Omit<CmsPublicationTitleListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...cmsKeys.publicationTitles.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<CmsPublicationTitle>>(cmsApi["publication-titles"].$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<CmsPublicationTitle>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** The list's figures — GET /publication-titles/stats (the announcementsStats precedent). */
  publicationTitlesStats: () =>
    queryOptions({
      queryKey: cmsKeys.publicationTitles.stats(),
      queryFn: () => unwrap<CmsContentStats>(cmsApi["publication-titles"].stats.$get()),
      staleTime: STALE.list,
    }),

  /** A single title — the edit form's defaultValues source, and the issue form's picker label for its stored titleId (404s on a trashed row). */
  publicationTitle: (id: string) =>
    queryOptions({
      queryKey: cmsKeys.publicationTitles.detail(id),
      queryFn: () => unwrap<CmsPublicationTitle>(cmsApi["publication-titles"][":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  /** The LIVE title at (slug, locale) in any status, or null — the sibling lookup (the publicationBySlug precedent). */
  publicationTitleBySlug: (slug: string, locale: string) =>
    queryOptions({
      queryKey: cmsKeys.publicationTitles.bySlug(slug, locale),
      queryFn: () => {
        const args = { param: { slug }, query: { locale } };
        return nullOn404(unwrap<CmsPublicationTitle>(cmsApi["publication-titles"]["by-slug"][":slug"].$get(args)));
      },
      staleTime: STALE.detail,
    }),

  // ── Gallery ──────────────────────────────────────────────────────────────────
  /** Keyset list, filters (?status/?collection/?accessLevel + cursor) ⊂ the key ⇒ the URL drives the cache. keepPreviousData across pages. */
  galleryItemsList: (filters: CmsGalleryItemListQuery) =>
    queryOptions({
      queryKey: cmsKeys.gallery.list(filters),
      queryFn: () => unwrap<KeysetPage<CmsGalleryItem>>(cmsApi.gallery.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),


  /** The gallery as an INFINITE stream — see `announcementsListInfinite`. */
  galleryItemsListInfinite: (filters: Omit<CmsGalleryItemListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...cmsKeys.gallery.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<CmsGalleryItem>>(cmsApi.gallery.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<CmsGalleryItem>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** The list's figures — GET /gallery/stats (the announcementsStats precedent). */
  galleryItemsStats: () =>
    queryOptions({
      queryKey: cmsKeys.gallery.stats(),
      queryFn: () => unwrap<CmsContentStats>(cmsApi.gallery.stats.$get()),
      staleTime: STALE.list,
    }),

  /** A single gallery item — the detail view + the edit form's defaultValues source (404s on a trashed row). */
  galleryItem: (id: string) =>
    queryOptions({
      queryKey: cmsKeys.gallery.detail(id),
      queryFn: () => unwrap<CmsGalleryItem>(cmsApi.gallery[":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  /** The LIVE gallery item at (slug, locale) in any status/access level, or null — the sibling lookup (the announcementBySlug precedent). */
  galleryItemBySlug: (slug: string, locale: string) =>
    queryOptions({
      queryKey: cmsKeys.gallery.bySlug(slug, locale),
      queryFn: () => {
        const args = { param: { slug }, query: { locale } };
        return nullOn404(unwrap<CmsGalleryItem>(cmsApi.gallery["by-slug"][":slug"].$get(args)));
      },
      staleTime: STALE.detail,
    }),

  // ── Media ────────────────────────────────────────────────────────────────────
  /**
   * The asset LIBRARY index — keyset list; filters (?kind/?visibility/?search + cursor)
   * ⊂ the key ⇒ the URL drives the cache, keepPreviousData across pages. Metadata ONLY
   * (no signed URLs — the serve mints those per row); the picker/library reads a row's
   * url via mediaServe, not from here.
   */
  mediaList: (filters: MediaListQuery) =>
    queryOptions({
      queryKey: cmsKeys.media.list(filters),
      queryFn: () => unwrap<KeysetPage<Media>>(cmsApi.media.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),


  /** The asset library as an INFINITE stream — the library GRID and the picker's grid both read it (the picker under its own page size, which is part of the key). Metadata only, exactly as `mediaList`. */
  mediaListInfinite: (filters: Omit<MediaListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...cmsKeys.media.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<Media>>(cmsApi.media.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<Media>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /**
   * The access-gated SERVE — GET /media/:id returns the metadata PLUS a short-lived
   * presigned GET url (public rows serve to anyone; private demands the cms:media grant,
   * enforced per-row server-side). The picker/library reads `.url` to render the asset;
   * 404s on a deleted row. Detail tier (a single row churns less than the filtered list).
   */
  mediaServe: (id: string) =>
    queryOptions({
      queryKey: cmsKeys.media.detail(id),
      queryFn: () => unwrap<MediaServe>(cmsApi.media[":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  // ── Categories ───────────────────────────────────────────────────────────────
  /**
   * The taxonomy narrowed by the optional ?type and ?locale (each omitted ⇒ every value) —
   * an UNPAGED bare array keyed by both (the lib branches(libraryId?) precedent). Reference
   * tier (rarely edited). The Categories admin table reads this with the URL's ?locale;
   * the forms' pickers and the sibling lookup read the ALL-locale entry (categoriesByType).
   */
  categoriesList,

  /**
   * The taxonomy for one content type (or every type when `type` is omitted), EVERY locale
   * — the category-picker on a content form (it filters to the row's locale client-side
   * and needs the other locale's rows to find a category's sibling) and the Categories
   * admin screen's sibling lookup both read this. The same cache entry as
   * categoriesList({ type }) — one definition, one key.
   */
  categoriesByType: (type?: CmsCategoryType) => categoriesList({ type }),

  // ── Settings ─────────────────────────────────────────────────────────────────
  /** The FULL configuration (rides cms:settings) — every key merged over DEFAULT_SETTINGS. Reference tier (config, rarely flipped). */
  settings: () =>
    queryOptions({
      queryKey: cmsKeys.settings.all(),
      queryFn: () => unwrap<CmsSettings>(cmsApi.settings.$get()),
      staleTime: STALE.reference,
    }),

  /**
   * The PUBLIC allowlisted subset — anonymous (NO guard); the static shell reads its identity
   * pre-sign-in — PLUS the locale block (defaultLocale / locales / localeNames), which every
   * content screen reads for its locale panel and filter (locale-fields.tsx). Reference tier.
   */
  settingsPublic: () =>
    queryOptions({
      queryKey: cmsKeys.settings.public(),
      queryFn: () => unwrap<CmsPublicSettings>(cmsApi.settings.public.$get()),
      staleTime: STALE.reference,
    }),

  // ── Newsletter subscribers ─────────────────────────────────────────────────────
  /**
   * The admin subscriber list — keyset; filters (?status/?search over email/name + cursor)
   * ⊂ the key ⇒ the URL drives the cache, keepPreviousData across pages. Admin-only (rides
   * cms:newsletter); there is no detail read (the API exposes only list + delete), so a row
   * is rendered from this page, never re-fetched singly. subscribe/unsubscribe are the
   * website's anonymous public writes, not read here.
   */
  subscribersList: (filters: CmsSubscriberListQuery) =>
    queryOptions({
      queryKey: cmsKeys.subscribers.list(filters),
      queryFn: () => unwrap<KeysetPage<CmsSubscriber>>(cmsApi.newsletter.subscribers.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),


  /** The subscriber roll as an INFINITE stream — see `announcementsListInfinite`. */
  subscribersListInfinite: (filters: Omit<CmsSubscriberListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...cmsKeys.subscribers.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<CmsSubscriber>>(cmsApi.newsletter.subscribers.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<CmsSubscriber>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** The list's figures — GET /newsletter/subscribers/stats: total / subscribed / unsubscribed, table-wide (no lifecycle, no trash). */
  subscribersStats: () =>
    queryOptions({
      queryKey: cmsKeys.subscribers.stats(),
      queryFn: () => unwrap<CmsSubscriberStats>(cmsApi.newsletter.subscribers.stats.$get()),
      staleTime: STALE.list,
    }),

  // ── Cookies / consent registry ─────────────────────────────────────────────────
  /**
   * The admin cookie registry — an UNPAGED bare array (a small SITE-CONFIG registry, not a
   * keyset feed), keyed by the optional ?category filter ⊂ the key ⇒ the filter drives the
   * cache. Reference tier (config, rarely edited — the categories/settings precedent). The
   * cookies admin screen reads this; the public consent banner reads its own grouped payload
   * (GET /public/cookies), not this admin list.
   */
  cookiesList: (filters: CmsCookieListQuery) =>
    queryOptions({
      queryKey: cmsKeys.cookies.list(filters),
      queryFn: () => unwrap<CmsCookie[]>(cmsApi.cookies.$get({ query: filters })),
      staleTime: STALE.reference,
    }),

  // ── Revisions (the per-record History log — db-cms 0013) ───────────────────────
  /**
   * One record's revision METADATA log (rev_no + action summary + audit stamps, NO
   * snapshot bodies — the list shape omits them). Keyed by the (type, id) pair, so a
   * record's History panel hits one cache entry; the panel reads the FIRST page (the
   * newest revisions), which is plenty for the log. List tier, keepPreviousData holds
   * the page while a restore-driven refetch lands.
   */
  revisionsList: (type: RevisionEntityType, id: string) =>
    queryOptions({
      queryKey: cmsKeys.revisions.list(type, id),
      queryFn: () => unwrap<KeysetPage<CmsRevision>>(revisionsListReq(type, id)),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /**
   * One revision's DETAIL — the parsed PIN-SAFE snapshot (the full admin DTO the record
   * held at that ordinal) behind the History panel's read-only "View". 404s on an unknown
   * revNo. Detail tier (a frozen snapshot never churns). Enable it only when a revision is
   * actually being viewed (the caller gates on the open revNo).
   */
  revision: (type: RevisionEntityType, id: string, revNo: number) =>
    queryOptions({
      queryKey: cmsKeys.revisions.detail(type, id, revNo),
      queryFn: () => unwrap<CmsRevisionDetail>(revisionReq(type, id, revNo)),
      staleTime: STALE.detail,
    }),

  // ── The reader (the member portal's ONE CMS read — GET /reading/:slug, 0024) ────────
  /**
   * The publication the member portal's /read/$slug shows, with its presigned file.
   * SESSION-AWARE on the wire (401 READING_SIGN_IN_REQUIRED / 403 READING_EMAIL_UNVERIFIED /
   * 403 READING_PIN_REQUIRED / 404) — every refusal is a UI state the screen maps to a card,
   * so `retry: false`: a 401 retried three times is still a 401, and the reader would sit
   * on a skeleton through the backoff. `?locale=` rides only when given (the route reads the
   * install default otherwise). The route validates nothing through hc, so `query` is
   * carried through a bound const (the by-slug precedent).
   *
   * THE URL EXPIRES (15 min, READING_GET_EXPIRES_S). staleTime AND refetchInterval are both
   * `readingRefreshInMs` over the answer: the interval re-mints the URL a minute before it
   * dies while the tab is open (a PDF viewer pages lazily, so a dead URL breaks mid-read,
   * not just at load); staleTime makes a remount, or a focus after a throttled background
   * tab slept past the window, re-read instead of trusting a URL that is already dead.
   * `Cache-Control: no-store` on the wire; the cache here lives only as long as the URL.
   */
  reading: (slug: string, locale?: string) =>
    queryOptions({
      queryKey: cmsKeys.reading.bySlug(slug, locale),
      queryFn: () => {
        // No `query` key at all without a locale: hc writes an empty one as a bare `?`.
        const args = locale ? { param: { slug }, query: { locale } } : { param: { slug } };
        return unwrap<CmsReadingResponse>(cmsApi.reading[":slug"].$get(args));
      },
      // The portal default already skips 4xx; `false` says the rule out loud
      // where the four refusals are the screen's own states.
      retry: false,
      // A row with no file has nothing that expires — the detail tier stands.
      staleTime: (query) => readingRefreshInMs(query.state.data) || STALE.detail,
      refetchInterval: (query) => readingRefreshInMs(query.state.data),
      // Nothing survives leaving the screen: the answer is PER VIEWER (a
      // sign-out or a verification in between changes it) and the wire says
      // `no-store`. A return re-asks — one row read and a presign, cheap next
      // to showing a document the server would now refuse.
      gcTime: 0,
    }),
} as const;

export type CmsQueries = typeof cmsQueries;
